import { useState, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Loader2, Search, History as HistoryIcon, User, Bot, RotateCcw } from 'lucide-react';
import api from '../services/api';
import { API } from '../App';

const ORANGE = '#ee7203';

const ACTION_BADGES = {
  auto_added: { label: 'Auto-Added', color: 'bg-sky-100 text-sky-700' },
  auto_planned: { label: 'Auto-Planned', color: 'bg-purple-100 text-purple-700' },
  moved: { label: 'Moved', color: 'bg-amber-100 text-amber-700' },
  unplanned: { label: 'Unplanned', color: 'bg-slate-200 text-slate-700' },
  status_changed: { label: 'Status', color: 'bg-blue-100 text-blue-700' },
  run_created: { label: 'Run Created', color: 'bg-green-100 text-green-700' },
  run_removed: { label: 'Run Removed', color: 'bg-red-100 text-red-700' },
  collection_flagged: { label: 'Collection', color: 'bg-orange-100 text-orange-700' },
  flagged_out_of_area: { label: 'Out of Area', color: 'bg-red-100 text-red-700' },
  held_today: { label: 'Today', color: 'bg-teal-100 text-teal-700' },
  held_next_day: { label: 'Next Day', color: 'bg-cyan-100 text-cyan-700' },
  entry_added: { label: 'Added', color: 'bg-green-100 text-green-700' },
  entry_removed: { label: 'Removed', color: 'bg-red-100 text-red-700' },
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const TransportHistoryTab = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', order_number: '', user: '', vehicle: '' });
  const [skip, setSkip] = useState(0);
  const LIMIT = 100;

  const load = useCallback(async (reset = true) => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, skip: reset ? 0 : skip };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get(`${API}/transport-history`, { params });
      setEntries(reset ? (res.data?.entries || []) : [...entries, ...(res.data?.entries || [])]);
      setTotal(res.data?.total || 0);
      if (reset) setSkip(0);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [filters, skip]);

  useEffect(() => { load(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = () => load(true);
  const clearFilters = () => {
    setFilters({ date_from: '', date_to: '', order_number: '', user: '', vehicle: '' });
    setTimeout(() => load(true), 0);
  };
  const loadMore = async () => {
    const nextSkip = skip + LIMIT;
    setSkip(nextSkip);
    await load(false);
  };

  return (
    <div className="space-y-4" data-testid="transport-history-tab">
      <Card>
        <CardContent className="pt-5 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <HistoryIcon className="w-4 h-4" style={{ color: ORANGE }} />
            Filter activity
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <div>
              <label className="text-xs text-gray-500">From date</label>
              <Input type="date" data-testid="history-filter-date-from" value={filters.date_from}
                onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500">To date</label>
              <Input type="date" data-testid="history-filter-date-to" value={filters.date_to}
                onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500">Order number</label>
              <Input placeholder="e.g. 3473290" data-testid="history-filter-order-number" value={filters.order_number}
                onChange={(e) => setFilters(f => ({ ...f, order_number: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500">User</label>
              <Input placeholder="e.g. Jamie" data-testid="history-filter-user" value={filters.user}
                onChange={(e) => setFilters(f => ({ ...f, user: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500">Vehicle</label>
              <Input placeholder="e.g. VX24 ZBW" data-testid="history-filter-vehicle" value={filters.vehicle}
                onChange={(e) => setFilters(f => ({ ...f, vehicle: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" data-testid="history-filter-apply" style={{ backgroundColor: ORANGE }} className="text-white"
              onClick={applyFilters}>
              <Search className="w-3.5 h-3.5 mr-1.5" /> Search
            </Button>
            <Button size="sm" variant="outline" data-testid="history-filter-clear" onClick={clearFilters}>
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-5">
          {loading && entries.length === 0 ? (
            <div className="flex items-center justify-center py-10 text-gray-400" data-testid="history-loading">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading activity...
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-10 text-gray-400" data-testid="history-empty-state">
              No matching activity found.
            </div>
          ) : (
            <div className="space-y-2" data-testid="history-entries-list">
              {entries.map((entry) => {
                const badge = ACTION_BADGES[entry.action_type] || { label: entry.action_type, color: 'bg-gray-100 text-gray-600' };
                return (
                  <div
                    key={entry.id}
                    data-testid={`history-entry-${entry.id}`}
                    className="flex items-start gap-3 border border-gray-100 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="mt-0.5 shrink-0">
                      {entry.actor_type === 'system' ? (
                        <Bot className="w-4 h-4 text-gray-400" data-testid={`history-entry-actor-system-${entry.id}`} />
                      ) : (
                        <User className="w-4 h-4 text-gray-400" data-testid={`history-entry-actor-user-${entry.id}`} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800" data-testid={`history-entry-description-${entry.id}`}>{entry.description}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-xs text-gray-400" data-testid={`history-entry-timestamp-${entry.id}`}>{fmtWhen(entry.occurred_at)}</span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge.color}`}>{badge.label}</span>
                        {entry.source_ui && entry.source_ui !== 'unknown' && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 uppercase">
                            {entry.source_ui}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {entries.length > 0 && entries.length < total && (
            <div className="flex justify-center pt-4">
              <Button size="sm" variant="outline" data-testid="history-load-more" onClick={loadMore} disabled={loading}>
                {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Load more ({entries.length} of {total})
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
